import type { AppId } from '@tnn/core'
import { useCallback } from 'react'
import {
  type NavigateFunction,
  type NavigateOptions,
  type NavigateProps,
  Navigate as RouterNavigate,
  type To,
  useNavigate as useRouterNavigate,
} from 'react-router'
import { useOptionalSession } from '../auth/session'
import { scopedPath } from './links'

/**
 * Every app is one router in Thananan OS (`startApps`), each under its path (`/tms`), and an app's pages still write their
 * links from their own root, as when each was its own site: `/waybills` in TMS is `/tms/waybills`. These put the app's
 * path in front of such a link, from the app whose pages they are in (`AppScope`); a path already under it, a full
 * address and a relative one are left as they are
 */

export function scopedTo(app: AppId, to: To): To {
  if (typeof to === 'string') return scopedPath(app, to)
  return to.pathname ? { ...to, pathname: scopedPath(app, to.pathname) } : to
}

/** The app whose pages these are (Thananan OS outside any, like the sign-in page) */
export function useScopeApp(): AppId {
  return useOptionalSession()?.appId ?? 'portal'
}

/** React Router's `useNavigate`, a path from the app's own root put under the app's path */
export function useNavigate(): NavigateFunction {
  const navigate = useRouterNavigate()
  const app = useScopeApp()
  return useCallback(
    ((to: To | number, options?: NavigateOptions) =>
      typeof to === 'number' ? navigate(to) : navigate(scopedTo(app, to), options)) as NavigateFunction,
    [navigate, app],
  )
}

/** React Router's `<Navigate>`, its `to` from the app's own root */
export function Navigate({ to, ...props }: NavigateProps) {
  const app = useScopeApp()
  return <RouterNavigate to={scopedTo(app, to)} {...props} />
}
